import { useCallback, useMemo } from 'react'
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native'
import { Check, X } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { formatPeriodEndShort } from '../../../accounts/helpers/billingDisplay.helper'
import { resolveCtaState, isCurrentPlan } from '../../helpers/pricingCtaState.helper'
import { shouldHideBusinessSelfServePricingUi } from '../../helpers/pricingProductionMode.helper'
import { invokePricingCtaAction } from '../../helpers/pricingCtaPress.helper'
import {
  PRICING_PLAN_TIERS,
  isConsumerProPricingPlanTier,
} from '../../constants/pricingPlans.constants'
import type { BusinessSeatBucket } from '../../constants/pricingPlans.constants'
import type { PricingCopyLabels } from '../../helpers/pricingI18n.helper'
import {
  CONSUMER_PRO_BILLING_INTERVAL,
  type ConsumerProBillingInterval,
} from '../../constants/consumerProBilling.constants'
import { getPricingScreenStyles } from './PricingScreen.styles'
import { SeatBucketPicker } from '../SeatBucketPicker/SeatBucketPicker'
import type { PricingPlanCard } from '../../constants/pricingPlans.constants'
import type { Theme } from '../../../theme/getTheme'

export interface PricingScreenPlanCardProps {
  plan: PricingPlanCard
  copy: PricingCopyLabels
  seatBuckets: readonly BusinessSeatBucket[]
  orgPlanTier: string | null
  consumerPlanTier: 'FREE' | 'PRO'
  consumerProBillingInterval: ConsumerProBillingInterval | null
  consumerCancelAtPeriodEnd: boolean
  consumerPeriodEndIso: string | null
  hasOrg: boolean
  isB2bOrgAdmin: boolean
  stripeNegotiatedPriceId: string | null
  stripeNegotiatedPriceDisplay: { main: string; sub: string } | null
  isSignedIn: boolean
  accountAdminLoading: boolean
  busyB2b: boolean
  busyConsumer: boolean
  orgB2bStripeSubscriptionPaid: boolean
  /** Same semantics as landing pricing — required for one-time Business upgrade vs manage CTAs. */
  orgMaxSeats: number | null
  selectedBucketMaxSeats: number | null
  theme: Theme
  styles: ReturnType<typeof getPricingScreenStyles>
  selectedBucketKey: string
  onSelectBucketKey: (key: string) => void
  onB2bCheckout: (tier: 'BUSINESS' | 'ENTERPRISE') => void
  onConsumerProCheckout: (billingInterval: ConsumerProBillingInterval) => void
  onContactSales: () => void
  onCreateOrg: () => void
  onSignIn: () => void
  onContinueToApp: () => void
  onManageConsumerSubscription: () => void
  onManageB2bBilling: () => void
  onScheduleDowngradeToMonthly: () => void
}

export function PricingScreenPlanCard({
  plan,
  copy,
  seatBuckets,
  orgPlanTier,
  consumerPlanTier,
  consumerProBillingInterval,
  consumerCancelAtPeriodEnd,
  consumerPeriodEndIso,
  hasOrg,
  isB2bOrgAdmin,
  stripeNegotiatedPriceId,
  stripeNegotiatedPriceDisplay,
  isSignedIn,
  accountAdminLoading,
  busyB2b,
  busyConsumer,
  orgB2bStripeSubscriptionPaid,
  orgMaxSeats,
  selectedBucketMaxSeats,
  theme,
  styles,
  selectedBucketKey,
  onSelectBucketKey,
  onB2bCheckout,
  onConsumerProCheckout,
  onContactSales,
  onCreateOrg,
  onSignIn,
  onContinueToApp,
  onManageConsumerSubscription,
  onManageB2bBilling,
  onScheduleDowngradeToMonthly,
}: PricingScreenPlanCardProps) {
  const isCurrent = isCurrentPlan(plan.tier, orgPlanTier, consumerPlanTier, consumerProBillingInterval)

  const proEndsOnLabel = useMemo(() => {
    if (plan.tier !== PRICING_PLAN_TIERS.PRO_MONTHLY || !consumerCancelAtPeriodEnd) return null
    return formatPeriodEndShort(consumerPeriodEndIso)
  }, [plan.tier, consumerCancelAtPeriodEnd, consumerPeriodEndIso])

  const showProEndsOnBadge =
    isCurrent
    && plan.tier === PRICING_PLAN_TIERS.PRO_MONTHLY
    && (consumerProBillingInterval == null
      || consumerProBillingInterval === CONSUMER_PRO_BILLING_INTERVAL.MONTH)
    && consumerCancelAtPeriodEnd
    && proEndsOnLabel != null
    && proEndsOnLabel.length > 0

  const ctaState = useMemo(
    () =>
      resolveCtaState(
        plan,
        orgPlanTier,
        consumerPlanTier,
        consumerProBillingInterval,
        hasOrg,
        isSignedIn,
        accountAdminLoading,
        busyB2b,
        busyConsumer,
        stripeNegotiatedPriceId,
        isB2bOrgAdmin,
        orgB2bStripeSubscriptionPaid,
        orgMaxSeats,
        selectedBucketMaxSeats,
        copy,
      ),
    [
      plan,
      orgPlanTier,
      consumerPlanTier,
      consumerProBillingInterval,
      hasOrg,
      isSignedIn,
      accountAdminLoading,
      busyB2b,
      busyConsumer,
      stripeNegotiatedPriceId,
      isB2bOrgAdmin,
      orgB2bStripeSubscriptionPaid,
      orgMaxSeats,
      selectedBucketMaxSeats,
      copy,
    ],
  )
  const { label: ctaLabel, action: ctaAction, disabled: ctaDisabled, outline } = ctaState
  const showCardCurrentHighlightBorder = isCurrent

  const proCardPriceParts = useMemo(() => {
    if (!plan.consumerProCheckout || !isConsumerProPricingPlanTier(plan.tier)) return null
    return { main: plan.priceLabel, sub: plan.priceSubLabel }
  }, [plan])

  const enterpriseNegotiatedPriceParts = useMemo(() => {
    if (plan.tier !== PRICING_PLAN_TIERS.ENTERPRISE || !stripeNegotiatedPriceDisplay) return null
    return { main: stripeNegotiatedPriceDisplay.main, sub: stripeNegotiatedPriceDisplay.sub }
  }, [plan.tier, stripeNegotiatedPriceDisplay])

  const isBusy =
    ctaAction === 'consumer-checkout' || ctaAction === 'manage-consumer-billing'
      ? busyConsumer
      : ctaAction === 'checkout'
        ? busyB2b
        : false

  const handlePress = useCallback(() => {
    invokePricingCtaAction(plan, ctaState, {
      onB2bCheckout,
      onConsumerProCheckout,
      onSignIn,
      onContinueToApp,
      onContactSales,
      onCreateOrg,
      onManageConsumerSubscription,
      onManageB2bBilling,
      onScheduleDowngradeToMonthly,
    })
  }, [
    plan,
    ctaState,
    onB2bCheckout,
    onConsumerProCheckout,
    onSignIn,
    onContinueToApp,
    onContactSales,
    onCreateOrg,
    onManageConsumerSubscription,
    onManageB2bBilling,
    onScheduleDowngradeToMonthly,
  ])

  const Icon = plan.icon
  const isProConsumerVisual = isConsumerProPricingPlanTier(plan.tier)
  const iconColor = isProConsumerVisual ? theme.white : theme.primary
  const hideBusinessSelfServePricing = shouldHideBusinessSelfServePricingUi(plan, orgPlanTier, hasOrg)

  return (
    <View
      style={[
        styles.card,
        showCardCurrentHighlightBorder && styles.cardHighlighted,
        isCurrent && styles.cardCurrent,
      ]}
    >
      <View style={styles.cardTopRow}>
        <View style={[styles.cardIconWrap, isProConsumerVisual && styles.cardIconWrapFeatured]}>
          <Icon size={22} color={iconColor} />
        </View>
        <View style={styles.badgeTopSlot}>
          {plan.comingSoon && !isCurrent ? (
            <View style={styles.badgeComingSoonPill}>
              <Text style={styles.badgeComingSoonPillText}>{copy.comingSoonBadge}</Text>
            </View>
          ) : null}
          {!plan.comingSoon && plan.mostPopular && !isCurrent ? (
            <View style={styles.badgeComingSoonPill}>
              <Text style={styles.badgeComingSoonPillText}>{copy.mostPopularBadge}</Text>
            </View>
          ) : null}
        </View>
      </View>

      <Text style={styles.cardName}>{plan.name}</Text>

      <View style={styles.cardPriceRow}>
        {hideBusinessSelfServePricing ? (
          <Text style={styles.cardPrice}>{copy.comingSoonInProduction}</Text>
        ) : enterpriseNegotiatedPriceParts ? (
          <>
            <Text style={[styles.cardPrice, styles.cardPriceHighlighted]}>
              {enterpriseNegotiatedPriceParts.main}
            </Text>
            {enterpriseNegotiatedPriceParts.sub ? (
              <Text style={styles.cardPriceSubHighlighted}>{` / ${enterpriseNegotiatedPriceParts.sub}`}</Text>
            ) : null}
          </>
        ) : proCardPriceParts ? (
          <>
            <Text style={[styles.cardPrice, styles.cardPriceHighlighted]}>{proCardPriceParts.main}</Text>
            {proCardPriceParts.sub ? (
              <Text style={styles.cardPriceSubHighlighted}>{` / ${proCardPriceParts.sub}`}</Text>
            ) : null}
          </>
        ) : plan.highlighted && plan.priceSubLabel ? (
          <>
            <Text style={[styles.cardPrice, styles.cardPriceHighlighted]}>{plan.priceLabel}</Text>
            <Text style={styles.cardPriceSubHighlighted}>{` / ${plan.priceSubLabel}`}</Text>
          </>
        ) : (
          <>
            <Text style={styles.cardPrice}>{plan.priceLabel}</Text>
            {plan.priceSubLabel ? <Text style={styles.cardPriceSub}>{plan.priceSubLabel}</Text> : null}
          </>
        )}
      </View>

      <Text style={styles.cardTagline}>{plan.tagline}</Text>

      {isCurrent ? (
        showProEndsOnBadge ? (
          <View style={[styles.badge, styles.badgeEndsScheduled]}>
            <Text style={styles.badgeEndsScheduledText}>
              {`${copy.proAccessEndsOn} ${proEndsOnLabel ?? ''}`}
            </Text>
          </View>
        ) : (
          <View style={[styles.badge, styles.badgeCurrent]}>
            <Text style={styles.badgeText}>{copy.currentPlanBadge}</Text>
          </View>
        )
      ) : null}

      <View style={styles.featureList}>
        {plan.features.map((f) => (
          <View key={f.label} style={styles.featureRow}>
            <View style={styles.featureIcon}>
              {f.included ? (
                <Check size={14} color={theme.success} strokeWidth={3} />
              ) : (
                <X size={14} color={theme.gray[400]} strokeWidth={2.5} />
              )}
            </View>
            <Text style={[styles.featureLabel, !f.included && styles.featureLabelExcluded]}>
              {f.label}
            </Text>
          </View>
        ))}
      </View>

      {plan.tier === PRICING_PLAN_TIERS.BUSINESS && !hideBusinessSelfServePricing ? (
        <SeatBucketPicker
          buckets={seatBuckets}
          selectedKey={selectedBucketKey}
          onSelect={onSelectBucketKey}
        />
      ) : null}

      {plan.tier === PRICING_PLAN_TIERS.BUSINESS && !hideBusinessSelfServePricing ? (
        <Text style={styles.orgPlanDisclosure}>{copy.orgPlanDisclosure}</Text>
      ) : null}

      {ctaAction !== 'none'
        && !(Platform.OS !== 'web' && (
          ctaAction === 'consumer-checkout'
          || ctaAction === 'manage-consumer-billing'
          || ctaAction === 'schedule-downgrade-to-monthly'
        )) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={ctaLabel}
          disabled={ctaDisabled}
          onPress={handlePress}
          style={({ pressed, hovered }) => [
            outline ? styles.ctaOutline : styles.cta,
            { flexDirection: 'row' },
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.ctaPressed,
            hovered && Platform.OS === 'web' && styles.ctaHoverWeb,
            ctaDisabled && styles.ctaDisabled,
          ]}
        >
          {isBusy ? (
            <ActivityIndicator
              size="small"
              color={outline ? theme.primary : theme.white}
              style={{ marginRight: 6 }}
            />
          ) : null}
          <Text style={outline ? styles.ctaOutlineText : styles.ctaText}>{ctaLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  )
}
